<template>
  <v-form
    ref="formRef"
    class="free-generator-shared-form method-form"
    @submit.prevent=""
  >
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">
          mdi-lightning-bolt-circle
        </v-icon>
        <span>Erzeugte Energieform</span>
      </div>

      <!-- Primär erzeugte Energieform -->
      <v-autocomplete
        v-model="form.mainGeneratedEnergy"
        :error-messages="validationErrors?.main_generated_energy"
        label="Primär erzeugte Energieform"
        :items="energies"
        item-title="name"
        item-value="id"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip
            v-if="hint.main_generated_energy"
            location="bottom"
            color="surface" 
            content-class="tooltip"
          >
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.main_generated_energy }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>

      <div class="shared-form-hint text-body-2 text-medium-emphasis">
        Diese Energieform gilt gemeinsam für Referenz und geplanten Zustand.
      </div>
    </div>
  </v-form>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import api from '@/plugins/axios'
import { cacheAdapter } from '@/cache'
import { useBaseForm } from '@/composables/useBaseForm'

/* Props from parent */
const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  reference:  { type: Boolean, default: false },
  planned:    { type: Boolean, default: false },
  otherData:  { type: Object, default: () => ({}) },
  sharedData: { type: Object, default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) },
  methodValue: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])

/* Field mapping — BACKEND KEYS (IMPORTANT FOR SHARED VALIDATION) */
const fields = {
  mainGeneratedEnergy: 'main_generated_energy',
}

/* useBaseForm integration for SHARED FORM */
const {
  form,
  hint,
  fetchHints,
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  defaults: {},
  reference: computed(() => props.reference),
  planned: computed(() => props.planned),
  shared: computed(() => true),        // <-- CRITICAL: this is a SHARED FORM
  otherData: computed(() => props.otherData),
  sharedData: computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (event, payload) => emit(event, payload),
})

/* Local list */
const energies = ref([])

/* API: fetch selectable energies (updated endpoint) */
async function fetchEnergies() {
  try {
    const { data } = await api.get('/methods/freegenerator/energies/', { adapter: cacheAdapter })
    energies.value = data
  } catch { /* optional: error toast */ }
}

/* expose validateForm() for parent error flow */
const formRef = ref(null)
function validateForm() {
  return formRef.value?.validate?.()
}

/* expose backend field list so parent can filter errors */
defineExpose({
  validateForm,
  fields: Object.values(fields),   // <-- PARENT USES THIS
})

/* lifecycle: load data + hints */
onMounted(async () => {
  await fetchEnergies()
  await fetchHints()
})
</script>

<style scoped lang="scss">
.free-generator-shared-form {
  width: 100%;
}

/* Input fields */
.free-generator-shared-form :deep(.v-field) {
  border-radius: 18px;
}

.free-generator-shared-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Visual section */
.method-section {
  margin-bottom: 0;
  padding: 1rem;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

/* Section heading */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;

  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;

  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);

  font-size: 1rem;
  font-weight: 700;
}

/* Hint icons */
.free-generator-shared-form :deep(.v-input__append),
.free-generator-shared-form :deep(.v-field__append-inner) {
  align-items: center;
}

.free-generator-shared-form :deep(.v-input__append .v-icon),
.free-generator-shared-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

/* Explanatory text */
.shared-form-hint {
  padding-left: 0.65rem;
  line-height: 1.45;
}

/* Responsive */
@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }
}
</style>
